/**
 * Slice 5 — `useFallbackPresentation` hook.
 *
 * The single integration point every database picker uses to render the
 * universal fallback presentation. Returns:
 *   - `entries` — ordered list `[config.database, ...knownDatabases]`,
 *     de-duplicated per-engine identifier-case rules. Empty when neither
 *     yields anything (callers may then fall back to the engine hardcoded
 *     list as a last resort).
 *   - `showFallback` — `true` iff the listing failed (`'error'` / `'empty'`)
 *     AND there's at least one actionable entry to render. Pickers gate
 *     their banner / per-entry indicator on this flag.
 *   - `isConfig(name)` / `isKnown(name)` — predicates for the
 *     `<UnverifiedDatabaseIndicator kind=…>` `kind` prop.
 *
 * Keeping the policy in one hook means picker components stay declarative —
 * the OE tree, Table Explorer dropdown, schema/data-compare pickers all
 * read the same shape.
 */

import { useMemo } from 'react';
import type { DatabasesListingState } from '../../database-objects/services/databasesService';
import { useConnectionStore } from '../../connections/store/connectionStore';
import { getLimitedDatabase } from '../../connections/utils/connectionDatabaseLimit';
import { isCaseInsensitiveDatabaseEngine } from '../../connections/utils/databaseNameCase';

type UseFallbackPresentationOptions = {
  connectionId: string | undefined;
  /** State returned by `getDatabases()` (`'ok'` / `'empty'` / `'error'`). */
  state: DatabasesListingState | undefined;
};

export type FallbackPresentation = {
  entries: string[];
  showFallback: boolean;
  isConfig: (name: string) => boolean;
  isKnown: (name: string) => boolean;
};

function caseFold(name: string, engine: string | undefined): string {
  return isCaseInsensitiveDatabaseEngine(engine) ? name.toLowerCase() : name;
}

export function useFallbackPresentation({
  connectionId,
  state,
}: UseFallbackPresentationOptions): FallbackPresentation {
  const connections = useConnectionStore((s) => s.connections);
  const activeConnections = useConnectionStore((s) => s.activeConnections);

  return useMemo<FallbackPresentation>(() => {
    if (!connectionId) {
      return {
        entries: [],
        showFallback: false,
        isConfig: () => false,
        isKnown: () => false,
      };
    }
    const saved = connections.find((c) => c.id === connectionId);
    const active = activeConnections.find((c) => c.id === connectionId);
    const connection = saved ?? active?.connection;
    const engine = connection?.engine;

    const configDb = connection?.database?.trim() || undefined;
    const known = connection?.knownDatabases ?? [];

    const seen = new Set<string>();
    const entries: string[] = [];
    if (configDb) {
      seen.add(caseFold(configDb, engine));
      entries.push(configDb);
    }
    // B3: a limited connection's fallback is EXACTLY its configured database —
    // never the wider `knownDatabases` (mirrors `buildSmartFallbackList` in
    // databasesFolderExpansion.ts). Inert when limited but no database is
    // configured — falls through to the normal known list below.
    const isLimited = getLimitedDatabase(connection) !== undefined;
    if (!isLimited) {
      for (const name of known) {
        const trimmed = name?.trim();
        if (!trimmed) continue;
        const folded = caseFold(trimmed, engine);
        if (seen.has(folded)) continue;
        seen.add(folded);
        entries.push(trimmed);
      }
    }

    const showFallback =
      (state === 'error' || state === 'empty') && entries.length > 0;

    const isConfig = (name: string): boolean => {
      if (!configDb) return false;
      return caseFold(name, engine) === caseFold(configDb, engine);
    };
    const isKnown = (name: string): boolean => {
      if (isConfig(name)) return false;
      const folded = caseFold(name, engine);
      return known.some((db) => caseFold(db, engine) === folded);
    };

    return { entries, showFallback, isConfig, isKnown };
  }, [connectionId, state, connections, activeConnections]);
}
